import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import aiApi from '../../../api/ai.api';
import RemoteResultHelper from '../../../helper/RemoteResultHelper';
import { loginActions } from '../../../redux/login';
import { applicationSelectors } from '../../../redux/application';
import { RemoteResult, RemoteResultConfiguration } from './index.types';

// `superseded` tells the caller that a NEWER evaluation (or an unmount) invalidated this one. All
// three entry points must stay silent in that case instead of reporting "nothing recognized" —
// otherwise the stale answer of a discarded photo would overwrite the reading of its successor.
export interface ImageEvaluationOutcome {
    result: RemoteResult | undefined;
    superseded: boolean;
}

// Single place where an image becomes a RemoteResult: fetch a fresh token, run the AI request under
// an abort controller with the configured timeout, map the response and resolve the meter number
// against the expected one. Used by all three entry points (camera shot, gallery pick, file upload),
// which previously carried three copies of this — with differing safeguards.
//
// Every failure mode (token, network, timeout, abort) yields a `result` of `undefined` so callers can
// treat "no result" uniformly: the photo is then attached without a reading.
export const useImageEvaluation = (config: RemoteResultConfiguration) => {
    const dispatch = useDispatch();
    const abortImageEvaluationAfterMs = useSelector(applicationSelectors.getAiImageEvaluationAbortAfterMs);
    const activeEvaluationRef = useRef<{ id: number; controller: AbortController; timeoutId: number } | null>(null);
    const evaluationIdRef = useRef(0);
    const isMountedRef = useRef(true);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Set again on every mount: React 18 StrictMode runs mount → cleanup → mount in development.
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;

            if (activeEvaluationRef.current) {
                window.clearTimeout(activeEvaluationRef.current.timeoutId);
                activeEvaluationRef.current.controller.abort();
                activeEvaluationRef.current = null;
            }
        };
    }, []);

    const evaluate = useCallback(
        async (imageBase64: string): Promise<ImageEvaluationOutcome> => {
            setLoading(true);

            try {
                let token: string;

                try {
                    token = await new Promise<string>((resolve, reject) => {
                        dispatch(loginActions.getFreshToken(config.accountId, { resolve, reject }));
                    });
                } catch (err) {
                    console.log('Error in useImageEvaluation - getFreshToken:', err);
                    return { result: undefined, superseded: false };
                }

                const evaluationId = ++evaluationIdRef.current;
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), abortImageEvaluationAfterMs);
                activeEvaluationRef.current = { id: evaluationId, controller, timeoutId };

                // A newer evaluation (or an unmount) invalidates this one: its answer is stale and
                // must not overwrite the newer reading.
                const isCurrentEvaluation = () =>
                    isMountedRef.current && activeEvaluationRef.current?.id === evaluationId && activeEvaluationRef.current.controller === controller;

                try {
                    config.usageCounter?.countEvaluate();
                    const res = await aiApi.evaluateImage(controller, token, { image: imageBase64 });

                    window.clearTimeout(timeoutId);

                    if (!isCurrentEvaluation()) {
                        return { result: undefined, superseded: true };
                    }

                    const mapped = RemoteResultHelper.mapImageEvaluationResponseToRemoteResult(res.data);

                    return { result: RemoteResultHelper.applyMeterNumberCandidateMatch(mapped, config), superseded: false };
                } catch (err: any) {
                    if (err?.name !== 'AbortError') {
                        console.log('Error in useImageEvaluation - evaluateImage:', err);
                    }

                    return { result: undefined, superseded: !isCurrentEvaluation() };
                } finally {
                    window.clearTimeout(timeoutId);

                    if (isCurrentEvaluation()) {
                        activeEvaluationRef.current = null;
                    }
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        },
        [abortImageEvaluationAfterMs, config, dispatch],
    );

    return { evaluate, loading };
};

export default useImageEvaluation;
